import { Suspense, useRef } from "react"
import { DefaultValues, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"
import { AgendaAction } from "@/modules/patients/agenda"
import { TimeWindowFooter } from "@/modules/patients/agenda/time-window/components/time-window-footer"
import { TimeWindowForm } from "@/modules/patients/agenda/time-window/components/time-window-form"
import { useHandleSubmit } from "@/modules/patients/agenda/time-window/hooks/useHandleSubmit"
import {
  AlternativeTriageData,
  initialTimeWindowState,
  TimeWindowFormSchema,
  timeWindowFormSchema,
} from "@/modules/patients/agenda/time-window/time-window-schema"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ReferredBy } from "@/types/__generated__/union-types"

export const OPEN_TIME_WINDOW_PARAM = "openTimeWindow"

export const OPEN_TIME_WINDOW_VALUE = {
  CREATE: "create",
  ALTERNATIVE_TRIAGE: "alternative-triage",
} as const

export type OpenTimeWindowValue = (typeof OPEN_TIME_WINDOW_VALUE)[keyof typeof OPEN_TIME_WINDOW_VALUE]

interface TimeWindowDrawerProps {
  setSelectedAgendaRowId?: React.Dispatch<React.SetStateAction<string | null>>
  mode: AgendaAction
  defaultReferredBy?: ReferredBy
  timeWindow?: TimeWindowFormSchema
  progressNoteId?: string | null
  alternativeTriageData?: AlternativeTriageData
}

const mapTimeWindowToForm = (timeWindow: TimeWindowFormSchema): TimeWindowFormSchema => ({
  id: timeWindow.id,
  referredBy: timeWindow.referredBy,
  referralToCareProviderId: timeWindow.referralToCareProviderId,
  nameOfDoctor: timeWindow.nameOfDoctor,
  referralReason: timeWindow.referralReason,
  dateFrom: new Date(timeWindow.dateFrom),
  dateTo: new Date(timeWindow.dateTo),
  internalComment: timeWindow.internalComment,
  involvesPreventionProgram: timeWindow.involvesPreventionProgram,
  referredByExternalCareProviderId: timeWindow.referredByExternalCareProviderId,
  referenceDate: timeWindow.referenceDate,
  correction: timeWindow.correction,
  createdAt: timeWindow.createdAt,
  updatedAt: timeWindow.updatedAt,
  alternativeTriage: timeWindow.alternativeTriage,
  alternativeTriagePhoneNumber: timeWindow.alternativeTriagePhoneNumber,
  alternativeTriageReason: timeWindow.alternativeTriageReason,
})

const TimeWindowDrawer: React.FC<TimeWindowDrawerProps> = ({
  setSelectedAgendaRowId,
  mode,
  defaultReferredBy,
  timeWindow,
  progressNoteId,
  alternativeTriageData,
}) => {
  const { t } = useTranslation()
  const { patient, isEditMode } = useOutletContext<{ patient: PatientFragment$data; isEditMode: boolean }>()

  const closeDrawer = useDrawerStore((store) => store.closeDrawer)

  const reasonSelectionRef = useRef<HTMLButtonElement>(null)

  const isViewMode = mode === "VIEW"

  const baseDefaultValues = timeWindow?.id
    ? mapTimeWindowToForm(timeWindow)
    : { ...initialTimeWindowState, referredBy: defaultReferredBy }

  const defaultValues: DefaultValues<TimeWindowFormSchema> = alternativeTriageData
    ? {
        ...baseDefaultValues,
        alternativeTriage: true,
        alternativeTriagePhoneNumber: alternativeTriageData.phoneNumber,
        alternativeTriageReason: alternativeTriageData.reason,
      }
    : baseDefaultValues

  const form = useForm<TimeWindowFormSchema>({
    resolver: zodResolver(timeWindowFormSchema),
    defaultValues,
    reValidateMode: "onChange",
    mode: "onChange",
  })

  const handleClose = () => {
    closeDrawer()
    setSelectedAgendaRowId?.(null)
  }

  // Focus the reason select once the drawer's slide-in animation finishes (avoids flickering during
  // the transition). Ignore bubbled child animations and the closing animation.
  const handleContentAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.currentTarget.getAttribute("data-state") !== "open") return
    reasonSelectionRef.current?.focus()
    form.trigger("referredBy")
  }

  const onSubmit = useHandleSubmit({ patient, onSuccess: handleClose })
  const handleSave = () => form.handleSubmit(onSubmit)()

  return (
    <Drawer defaultOpen={false} open onOpenChange={handleClose}>
      <DrawerContent
        className="shadow-1 relative border"
        data-testid="time-window-drawer"
        onAnimationEnd={handleContentAnimationEnd}>
        <DrawerClose className="top-3 right-2" />
        <DrawerHeader className="mb-4 h-16 py-4.5 pr-4">
          <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued mb-15 text-sm font-medium">
            {t("patient.agenda.timeWindow")}
          </DrawerTitle>
        </DrawerHeader>
        <FormProvider {...form}>
          <DrawerBody className="wrapper-container group/scrollbar grid grid-rows-1">
            <div className="h-full">
              <Suspense>
                <TimeWindowForm
                  patient={patient}
                  mode={mode}
                  timeWindow={timeWindow}
                  progressNoteId={progressNoteId}
                  reasonTriggerRef={reasonSelectionRef}
                />
              </Suspense>
            </div>
          </DrawerBody>
          <TimeWindowFooter
            timeWindow={timeWindow}
            isViewMode={isViewMode}
            isEditMode={isEditMode}
            onSave={handleSave}
            onClose={handleClose}
          />
        </FormProvider>
      </DrawerContent>
    </Drawer>
  )
}

export { TimeWindowDrawer }
